// pages/DashboardPage.tsx

import { useAdminDashboard } from "../../hooks/dashboard/useDashboard";
import PageLayout from "../../layouts/PageListLayout";

export default function DashboardPage() {
  const { data, loading, error } = useAdminDashboard();

  if (loading) {
    return (
      <PageLayout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Tableau de bord">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          Erreur : {error}
        </div>
      </PageLayout>
    );
  }

  if (!data) return null;

  return (
    <PageLayout title="Tableau de bord">
      
      {/* ===== RÉSUMÉ ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {data.summary.items.map((item, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm text-gray-500">{item.label}</p>
            <p className="text-2xl font-bold text-gray-900">{item.value}</p>
          </div>
        ))}
      </div>

      {/* ===== COMMANDES ===== */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500">Total commandes</p>
          <p className="text-2xl font-bold text-gray-900">{data.orders.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-sm border border-yellow-200 p-5">
          <p className="text-sm text-yellow-700">En attente</p>
          <p className="text-2xl font-bold text-yellow-700">{data.orders.pending}</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-5">
          <p className="text-sm text-green-700">Approuvées</p>
          <p className="text-2xl font-bold text-green-700">{data.orders.approved}</p>
        </div>
        <div className="bg-red-50 rounded-xl shadow-sm border border-red-200 p-5">
          <p className="text-sm text-red-700">Annulées</p>
          <p className="text-2xl font-bold text-red-700">{data.orders.canceled}</p>
        </div>
      </div>

      {/* ===== PRODUITS ===== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500">Total produits</p>
          <p className="text-2xl font-bold text-gray-900">{data.products.total}</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-5">
          <p className="text-sm text-green-700">Actifs</p>
          <p className="text-2xl font-bold text-green-700">{data.products.active}</p>
        </div>
        <div className="bg-orange-50 rounded-xl shadow-sm border border-orange-200 p-5">
          <p className="text-sm text-orange-700">Stock faible</p>
          <p className="text-2xl font-bold text-orange-700">{data.products.lowStock}</p>
        </div>
      </div>

      {/* ===== DERNIÈRES COMMANDES ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Dernières commandes</h3>
          <div className="space-y-3">
            {data.recentOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucune commande récente</p>
            ) : (
              data.recentOrders.map((order) => (
                <div
                  key={order.reference}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border border-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-800">{order.reference}</p>
                    <p className="text-sm text-gray-500">{order.client}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{order.amount}</p>
                    <p className={`text-xs ${
                      order.status === 'Approuvée' ? 'text-green-600' :
                      order.status === 'Annulée' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {order.status}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ===== TOP PRODUITS ===== */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Produits les plus vendus</h3>
          <div className="space-y-3">
            {data.topProducts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucune vente enregistrée</p>
            ) : (
              data.topProducts.map((product, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border border-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-800">{product.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{product.sold} vendus</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}